import React from 'react';
import { Box, Typography, FormControl, Select, MenuItem, SelectChangeEvent } from '@mui/material';

interface MentalHealthAssessmentProps {
  type: 'PHQ-9' | 'GAD-7';
  scores: (number | undefined)[];
  onChange: (scores: number[]) => void;
  total: number;
}

const phq9Questions = [
  "Little interest or pleasure in doing things?",
  "Feeling down, depressed, or hopeless?",
  "Trouble falling or staying asleep, or sleeping too much?",
  "Feeling tired or having little energy?",
  "Poor appetite or overeating?",
  "Feeling bad about yourself — or that you are a failure or have let yourself or your family down?",
  "Trouble concentrating on things, such as reading the newspaper or watching television?",
  "Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving a lot more than usual?",
  "Thoughts that you would be better off dead or of hurting yourself in some way?"
];

const gad7Questions = [
  "Feeling nervous, anxious, or on edge?",
  "Not being able to stop or control worrying?",
  "Worrying too much about different things?",
  "Trouble relaxing?",
  "Being so restless that it's hard to sit still?",
  "Becoming easily annoyed or irritable?",
  "Feeling afraid as if something awful might happen?"
];

const scoreOptions = [
  { value: '', label: 'Select...' },
  { value: 0, label: '0 - Not at all' },
  { value: 1, label: '1 - Several days' },
  { value: 2, label: '2 - More than half the days' },
  { value: 3, label: '3 - Nearly every day' }
];

const MentalHealthAssessment: React.FC<MentalHealthAssessmentProps> = ({
  type,
  scores,
  onChange,
  total
}) => {
  const questions = type === 'PHQ-9' ? phq9Questions : gad7Questions;
  const maxScore = type === 'PHQ-9' ? 27 : 21;

  const handleScoreChange = (index: number, event: SelectChangeEvent<number>) => {
    const value = event.target.value;
    const newScores = [...scores];
    newScores[index] = value === '' ? undefined : Number(value);
    onChange(newScores.filter((s): s is number => s !== undefined));
  };

  const getSeverityLevel = (score: number, type: 'PHQ-9' | 'GAD-7') => {
    if (type === 'PHQ-9') {
      if (score <= 4) return { text: 'Minimal', color: '#4caf50' };
      if (score <= 9) return { text: 'Mild', color: '#8bc34a' };
      if (score <= 14) return { text: 'Moderate', color: '#ff9800' };
      if (score <= 19) return { text: 'Moderately Severe', color: '#ff5722' };
      return { text: 'Severe', color: '#f44336' };
    } else {
      if (score <= 4) return { text: 'Minimal', color: '#4caf50' };
      if (score <= 9) return { text: 'Mild', color: '#8bc34a' };
      if (score <= 14) return { text: 'Moderate', color: '#ff9800' };
      return { text: 'Severe', color: '#f44336' };
    }
  };

  const severity = getSeverityLevel(total, type);

  return (
    <Box className="glass-card-light p-6 rounded-lg shadow">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 600 }}>
          {type} Assessment
        </Typography>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h4" className="theme-text-primary" sx={{ fontWeight: 700 }}>
            {total}
            <Typography component="span" variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 0.5 }}>
              / {maxScore}
            </Typography>
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: severity.color,
              fontWeight: 600,
              display: 'block'
            }}
          >
            {severity.text}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {questions.map((question, index) => (
          <Box key={index}>
            <Typography 
              variant="body2" 
              className="theme-text-primary" 
              sx={{ mb: 1, fontWeight: 500 }}
            >
              {index + 1}. {question}
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={scores[index] ?? ''}
                onChange={(e) => handleScoreChange(index, e)}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea'
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              >
                {scoreOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MentalHealthAssessment;
